/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.SYSTEM.ASSETS
 *
 * Branding asset uploads. Browser-side code can't write to the project's
 * /public folder (that's a build-time-only concept), so this page uploads
 * to a Supabase Storage bucket called "branding" and surfaces each file's
 * public URL. Admins paste those URLs into csm_system_parameters values such
 * as LOGIN_BACKGROUND - or use the inline "Use as..." actions below.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { getForensicActorId } from '../../types/auth';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { compareStrings, useSort } from '../../lib/use_sort';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw, Upload } from 'lucide-react';

/** File extension (lowercase, no dot) — the asset list's one logical facet. */
function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toLowerCase() : '';
}

const BUCKET = 'branding';

interface FileRow {
  name: string;
  size: number;
  url: string;
  updated_at: string;
}

type AssetSortKey = 'name' | 'size' | 'updated_at';

const FILTER_KEY = 'csm.assets.filter';

const ASSET_GRID_COLS: GridColumnDef[] = [
  { key: 'name',       header: 'File',       defaultWidth: 240 },
  { key: 'size',       header: 'Size',       defaultWidth: 100 },
  { key: 'updated_at', header: 'Updated',    defaultWidth: 180 },
  { key: 'url',        header: 'Public URL', defaultWidth: 320 },
];

export interface AssetUploadPageProps {
  session: AuthSession;
}

export function AssetUploadPage({ session }: AssetUploadPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.SYSTEM.ASSETS" session={session}>
      <AssetUploadPageInner session={session} />
    </PersonalizationProvider>
  );
}

function AssetUploadPageInner({ session }: AssetUploadPageProps) {
  const { t } = useTranslation();
  const [files, setFiles]   = useState<FileRow[]>([]);
  const [filter, setFilter] = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facet (file type / extension).
  const [facetType, setFacetType] = useState<string[]>([]);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState<string | null>(null);
  const [okMsg, setOkMsg]   = useState<string | null>(null);
  const sort = useSort<AssetSortKey>('csm.assets.sort', { key: 'updated_at', dir: 'desc' });
  const detail = useExpandedRows<string>();

  // TODO: wire relations — a branding storage object is a flat file row with no
  // child collections to surface; the disclosure renders inert (spacer).
  const buildRelatedGroups = (_name: string): RelatedChildGroup[] => [];
  const hasRelated = (name: string) => buildRelatedGroups(name).some(g => g.items.length > 0);

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.storage
        .from(BUCKET)
        .list('', { sortBy: { column: 'created_at', order: 'desc' }, limit: 200 });
      if (e) throw e;

      type StorageObject = {
        name: string;
        updated_at?: string | null;
        created_at?: string | null;
        metadata?: { size?: number } | null;
      };
      const rows: FileRow[] = ((data ?? []) as StorageObject[])
        .filter((f) => f.name && !f.name.endsWith('/'))
        .map((f) => {
          const { data: pub } = apiDb.storage.from(BUCKET).getPublicUrl(f.name);
          return {
            name: f.name,
            size: f.metadata?.size ?? 0,
            url: pub.publicUrl,
            updated_at: f.updated_at ?? f.created_at ?? '',
          };
        });
      setFiles(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const fileList = input.files;
    if (!fileList || fileList.length === 0) return;
    setBusy(true); setError(null); setOkMsg(null);
    try {
      for (const file of Array.from(fileList)) {
        const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const { error: e } = await apiDb.storage
          .from(BUCKET)
          .upload(cleanName, file, { cacheControl: '3600', upsert: true });
        if (e) throw e;
      }
      setOkMsg(fileList.length === 1 ? t('asset_upload.list.toast.uploaded_one', { count: fileList.length }) : t('asset_upload.list.toast.uploaded_other', { count: fileList.length }));
      input.value = '';
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (name: string) => {
    if (!confirm(t('asset_upload.list.errors.confirm_delete', { name }))) return;
    setBusy(true); setError(null); setOkMsg(null);
    try {
      const { error: e } = await apiDb.storage.from(BUCKET).remove([name]);
      if (e) throw e;
      setOkMsg(t('asset_upload.list.toast.deleted', { name }));
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setOkMsg(t('asset_upload.list.toast.url_copied'));
      setTimeout(() => setOkMsg(null), 2500);
    } catch {
      setError(t('asset_upload.list.errors.clipboard_failed'));
    }
  };

  /** One-click: set this URL as the value of a known system parameter. */
  const setAsParameter = async (paramCode: string, url: string) => {
    setBusy(true); setError(null); setOkMsg(null);
    try {
      const actorId = getForensicActorId(session);
      const { error: e } = await apiDb
        .from('csm_system_parameters')
        .update({ parameter_value: url, modified_by: actorId })
        .eq('internal_code', paramCode);
      if (e) throw e;
      setOkMsg(t('asset_upload.list.toast.param_set', { code: paramCode, url }));
      setTimeout(() => setOkMsg(null), 4000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Distinct file-type options derived from the loaded rows (§17.B5).
  const typeOptions = useMemo(
    () => [...new Set(files.map(f => fileExt(f.name)).filter(Boolean))].sort().map(v => ({ value: v, label: v.toUpperCase() })),
    [files],
  );
  const anyFilterActive = filter.trim().length > 0 || facetType.length > 0;
  const clearAll = () => { setFilter(''); setFacetType([]); };

  const filtered = (() => {
    const q = filter.trim().toLowerCase();
    const base = files
      .filter(f => !q || f.name.toLowerCase().includes(q))
      .filter(f => facetType.length === 0 || facetType.includes(fileExt(f.name)));
    return sort.sorted(base, (a, b, key) => {
      if (key === 'size')       return a.size - b.size;
      if (key === 'updated_at') return compareStrings(a.updated_at, b.updated_at);
      return compareStrings(a.name, b.name);
    });
  })();

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('asset_upload.list.title', 'Branding Assets')}
          description={t('asset_upload.list.description', 'Upload branding images and copy their public URLs into system parameters.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              <label className="btn-primary flex cursor-pointer items-center gap-1.5">
                <Upload className="h-3.5 w-3.5" /> {t('asset_upload.list.upload')}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                  multiple
                  onChange={onUpload}
                  className="hidden"
                />
              </label>
            </>
          }
        />

        {error && (
          <ErrorBanner
            message={t('asset_upload.list.load_failed', "Couldn't complete the asset operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Toolbar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('asset_upload.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('asset_upload.list.columns.type', 'Type')} options={typeOptions} selected={facetType} onChange={setFacetType} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-watermark">{t('common.working')}</span>}
              {okMsg && <span className="text-csm-xs text-csm-success">{okMsg}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          {/* Grid */}
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={ASSET_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border bg-csm-surface-alt px-1 py-1.5" title={t('asset_upload.list.related.header', 'Related records')} />
                  <th className="border-b border-csm-border px-3 py-1.5 text-left">{t('asset_upload.list.columns.preview')}</th>
                  {visibleCols.map(col => {
                    if (col.key === 'name')       return <SortableHeader<AssetSortKey> key={col.key} sortKey="name"       label={t('asset_upload.list.columns.file')}    currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />;
                    if (col.key === 'size')       return <SortableHeader<AssetSortKey> key={col.key} sortKey="size"       label={t('asset_upload.list.columns.size')}    currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />;
                    if (col.key === 'updated_at') return <SortableHeader<AssetSortKey> key={col.key} sortKey="updated_at" label={t('asset_upload.list.columns.updated')} currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort} />;
                    return (
                      <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('asset_upload.list.columns.public_url')}</th>
                    );
                  })}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('asset_upload.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(f => (
                  <Fragment key={f.name}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(f.name)}
                        hasChildren={hasRelated(f.name)}
                        onToggle={() => detail.toggle(f.name)}
                        expandLabel={t('asset_upload.list.related.expand', 'Show related records')}
                        collapseLabel={t('asset_upload.list.related.collapse', 'Hide related')}
                        tooltip={t('asset_upload.list.related.tooltip', 'Show related records')}
                      />
                    </td>
                    <td className="border-b border-csm-border-soft px-3 py-1.5">
                      <img
                        src={f.url}
                        alt={f.name}
                        className="h-12 w-20 rounded border border-csm-border bg-white object-contain"
                        // Transparent-image checkerboard preview. Color uses
                        // --color-border-soft so it tracks the active theme.
                        style={{
                          backgroundImage:
                            'linear-gradient(45deg, var(--color-border-soft) 25%, transparent 25%),' +
                            'linear-gradient(-45deg, var(--color-border-soft) 25%, transparent 25%),' +
                            'linear-gradient(45deg, transparent 75%, var(--color-border-soft) 75%),' +
                            'linear-gradient(-45deg, transparent 75%, var(--color-border-soft) 75%)',
                          backgroundSize: '8px 8px',
                          backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                        }}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'name'       && <span className="font-mono text-csm-sm">{f.name}</span>}
                        {col.key === 'size'       && <span className="csm-num">{(f.size / 1024).toFixed(1)} {t('asset_upload.list.size_unit')}</span>}
                        {col.key === 'updated_at' && <span className="text-csm-xs text-csm-ink-soft">{f.updated_at ? new Date(f.updated_at).toLocaleString() : ''}</span>}
                        {col.key === 'url'        && <div className="font-mono text-csm-2xs text-csm-ink-soft truncate">{f.url}</div>}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => void copyUrl(f.url)}
                        className="mr-1 inline-block min-w-[4rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft"
                      >{t('asset_upload.list.actions.copy_url')}</button>
                      <RowActionsMenu
                        label={t('common.row_actions', 'Row actions')}
                        actions={[
                          { key: 'open', label: t('asset_upload.list.actions.open'), onSelect: () => { window.open(f.url, '_blank', 'noreferrer'); } },
                          { key: 'use_as_login', label: t('asset_upload.list.actions.use_as_login'), onSelect: () => void setAsParameter('LOGIN_BACKGROUND', f.url) },
                          { key: 'use_as_mobile_login', label: t('asset_upload.list.actions.use_as_mobile_login'), onSelect: () => void setAsParameter('MOBILE_LOGIN_BACKGROUND', f.url) },
                          { key: 'delete', label: t('asset_upload.list.actions.delete'), onSelect: () => void onDelete(f.name), danger: true },
                        ]}
                      />
                    </td>
                  </tr>
                  {detail.isOpen(f.name) && (
                    <tr>
                      <td colSpan={visibleCols.length + 3} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(f.name)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 3} className="px-3 py-4">
                    {anyFilterActive
                      ? <EmptyState variant="no-match" title={t('asset_upload.list.empty_filtered')} />
                      : (
                        <EmptyState
                          variant="no-data"
                          title={t('asset_upload.list.empty_initial')}
                          action={
                            <label className="flex h-7 cursor-pointer items-center rounded border border-primary bg-primary px-3 text-csm-sm text-white hover:bg-primary-hover">
                              {t('asset_upload.list.upload')}{` `}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                                multiple
                                onChange={onUpload}
                                className="hidden"
                              />
                            </label>
                          }
                        />
                      )}
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
